import React, {
  useState,
  useEffect,
  useCallback,
  MouseEvent,
} from 'react';
import {
  useImpersonation,
} from 'core/context/impersonation';
import {
  useAuth,
  useConfirmDialog,
} from 'core/hooks';
import OrgSelect from 'views/EMR/PopulateSupport/OrgSelect';
import UserList from 'views/EMR/PopulateSupport/UserList';
import ImpersonateDialog from 'views/EMR/PopulateSupport/ImpersonateDialog';
import ImpersonationActiveDialog from 'views/EMR/PopulateSupport/ImpersonationActiveDialog';
import SupportDocs from 'views/EMR/PopulateSupport/SupportDocs';
import SupportPortalLanding, {
  SupportPortalSection,
} from 'views/EMR/PopulateSupport/SupportPortalLanding';
import SupportPortalQueryExecutionSection from 'views/EMR/PopulateSupport/SupportPortalQueryExecutionSection';
import {
  supportDocs,
} from 'views/EMR/PopulateSupport/supportDocsContent';
import {
  PopulateSupportOrganizationDTO,
  PopulateSupportUserDTO,
} from 'dtos';
import {
  APP_ROUTES,
} from 'views/EMR/router';
import PopulateLogo from 'core/icons/populateLogo';
import {
  notEmptyString,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import {
  populateSupportEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  usePopulateSupportGetAllOrganizations,
  usePopulateSupportGetUsersByOrganization,
  usePopulateSupportImpersonateUser,
} from 'views/EMR/PopulateSupport/useHook';
import Skeleton from 'core/components/Skeleton';
import Button from 'pages/Dashboard/components/Button';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';

type PortalView = 'landing' | SupportPortalSection;

export default function SupportPortal() {
  const {
    startImpersonation,
  } = useImpersonation();

  const {
    confirm,
    closeConfirm,
  } = useConfirmDialog();
  const { logout } = useAuth();

  const [activeView, setActiveView] = useState<PortalView>('landing');

  const [selectedOrg, setSelectedOrg] = useState<PopulateSupportOrganizationDTO | null>(null);
  const [selectedUser, setSelectedUser] = useState<PopulateSupportUserDTO | null>(null);

  const [impersonateError, setImpersonateError] = useState<string | null>(null);

  const {
    data: organizations = [],
    isLoading: isLoadingOrgs,
    error: orgsQueryError,
  } = usePopulateSupportGetAllOrganizations();

  const {
    data: users = [],
    isLoading: isLoadingUsers,
    error: usersQueryError,
  } = usePopulateSupportGetUsersByOrganization(selectedOrg?.organizationId ?? 0);

  const {
    mutateAsync: impersonateUser,
    isPending: isImpersonatePending,
    isSuccess: isImpersonateSuccess,
  } = usePopulateSupportImpersonateUser();
  const isImpersonateLoading = isImpersonatePending || isImpersonateSuccess;

  const orgsError = (() => {
    const error: any = orgsQueryError as any;
    if (isNil(error)) return null;
    if (error.response?.status === 403) {
      return 'Access denied. You are not authorized to use the support portal.';
    }
    return 'Failed to load organizations. Please try again.';
  })();

  const usersError = (() => {
    const error: any = usersQueryError as any;
    if (isNil(error)) return null;
    return 'Failed to load users. Please try again.';
  })();

  const onLogout = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    confirm({
      title: 'Logout',
      onConfirm: async () => {
        await logout({ returnTo: `${window.location.origin}${APP_ROUTES.POPULATE_SUPPORT.ROOT}` });
        closeConfirm();
      },
      content: 'Are you sure you want to logout?',
    });
  };

  const handleOrgSelect = useCallback((org: PopulateSupportOrganizationDTO | null) => {
    setSelectedOrg(org);
    setSelectedUser(null);
  }, []);

  const handleUserSelect = useCallback((user: PopulateSupportUserDTO) => {
    setSelectedUser(user);
    setImpersonateError(null);
  }, []);

  const handleImpersonate = useCallback(async () => {
    if (!selectedUser) return;

    setImpersonateError(null);

    try {
      const impersonationResponse = await impersonateUser({
        userId: selectedUser.userId,
      });

      if (impersonationResponse) {
        startImpersonation();
        window.location.replace(APP_ROUTES.EMR.ROOT);
      } else {
        setImpersonateError('Failed to start impersonation. Invalid response.');
      }
    } catch (error: any) {
      if (error.response?.status === 403) {
        setImpersonateError('Access denied. You are not authorized to impersonate users.');
      } else if (error.response?.status === 404) {
        setImpersonateError('User not found.');
      } else {
        setImpersonateError('An error occurred while starting impersonation.');
      }
    }
  }, [impersonateUser, selectedUser, startImpersonation]);

  const handleCloseDialog = useCallback(() => {
    setSelectedUser(null);
    setImpersonateError(null);
  }, []);

  const handleSectionChange = useCallback((section: SupportPortalSection) => {
    setActiveView(section);
  }, []);

  const handleBackToLanding = useCallback(() => {
    setActiveView('landing');
  }, []);

  useEffect(() => {
    const unsubscribe = populateSupportEventChannel.on(
      'onImpersonateUser',
      (user: PopulateSupportUserDTO) => {
        handleUserSelect(user);
        setActiveView('impersonation');
      },
    );
    return () => { unsubscribe(); };
  }, [handleUserSelect]);

  return (
    <div className="h-screen bg-slate-50 flex flex-col">
      <div className="sticky top-0 z-20 w-full shadow-lg backdrop-blur">
        <div className="flex flex-col w-full">
          <div className="w-full bg-amber-500 py-4 px-4 md:px-8">
            <div className="max-w-md md:max-w-6xl mx-auto">
              <header className="flex flex-col md:gap-1">
                <div className="flex items-center justify-between w-full">
                  <div className="flex flex-col gap-1">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <PopulateLogo className="!w-40 !h-auto !text-slate-900" />
                        <p className="text-2xl font-bold text-slate-900">
                          Support Portal
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={onLogout}
                    className="flex-1 !text-white/90 !py-2 !px-2 hover:!text-white hover:!bg-white/10 flex gap-1 items-center"
                  >
                    <LogoutRoundedIcon className="!w-5 !h-5" />
                    Log Out
                  </Button>
                </div>
              </header>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="max-w-md md:max-w-6xl mx-auto h-full overflow-auto px-4 md:px-0">
          <div className="pt-6">
            {activeView === 'landing' && (
              <SupportPortalLanding onSelectSection={handleSectionChange} />
            )}

            {activeView !== 'landing' && (
              <div className="flex flex-col gap-4 pb-2">
                <Button
                  variant="text"
                  color="primary"
                  onClick={handleBackToLanding}
                  className="!w-fit !px-2 !py-1 !text-slate-700 hover:!bg-slate-100"
                >
                  <ArrowBackRoundedIcon className="!w-5 !h-5" />
                  Back to Support Portal
                </Button>

                {activeView === 'impersonation' && (
                  <div className="flex flex-col gap-4">
                    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                      <div className="flex flex-col gap-1">
                        <h2 className="text-base font-semibold text-slate-900">Impersonation</h2>
                        <p className="text-xs text-slate-500">
                          Select an organization to view and impersonate users.
                        </p>
                      </div>
                    </div>
                    {notEmptyString(orgsError) ? (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                        {orgsError}
                      </div>
                    ) : (
                      <>
                        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                          <div className="flex justify-between gap-4">
                            <div>
                              <h3 className="text-base font-semibold">Organization</h3>
                              <p className="text-xs text-slate-500">Select an organization to view users</p>
                            </div>
                            {isLoadingOrgs ? (
                              <Skeleton className="!h-8 !w-96" />
                            ) : (
                              <OrgSelect
                                organizations={organizations}
                                selectedOrg={selectedOrg}
                                onSelect={handleOrgSelect}
                              />
                            )}
                          </div>
                        </div>
                        {!isNil(selectedOrg) && (
                          <div className="bg-white rounded-lg shadow-sm border border-slate-200 flex-1 flex flex-col overflow-hidden">
                            {notEmptyString(usersError) ? (
                              <div className="mb-4 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
                                {usersError}
                              </div>
                            ) : (
                              <UserList
                                users={users}
                                isLoading={isLoadingUsers}
                              />
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {activeView === 'knowledgeBase' && (
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                    <div className="flex flex-col gap-1 mb-4">
                      <h2 className="text-base font-semibold text-slate-900">How-To / Knowledge Base</h2>
                      <p className="text-xs text-slate-500">
                        Internal guidance for support workflows.
                      </p>
                    </div>
                    <SupportDocs docs={supportDocs} />
                  </div>
                )}

                {activeView === 'queryExecution' && (
                  <SupportPortalQueryExecutionSection />
                )}
              </div>
            )}
          </div>
          {!isNil(selectedUser) && (
            <ImpersonateDialog
              user={selectedUser}
              organizationName={selectedOrg?.displayName ?? ''}
              isLoading={isImpersonateLoading}
              error={impersonateError}
              onConfirm={handleImpersonate}
              onClose={handleCloseDialog}
            />
          )}
          {!isImpersonateLoading && (
            <ImpersonationActiveDialog />
          )}
        </div>
      </div>
    </div>
  );
}
